import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, null as nullFactory, number, strictObject, string, union } from "../../lazy-zod.js";
import { agentProfileIdSchema, uuidProfileIdSchema } from "../protocol/profile.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { authProfileManagedRoleSchema } from "./profiles.js";
import { agentProfileWorkRecoveryActionKindSchema } from "../agent-profile-work-recovery.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
import { agentProfileBindingModelSelectionSchema } from "./agent-profile-binding.js";
import { getAgentExecutionReadinessResponseSchema } from "./agent-execution-readiness.js";
//#region src/schemas/http/organization-directory.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const trimmedNonEmptyStringSchema = string().trim().min(1);
const spaceMemberWebActivityWriteSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	userId: trimmedNonEmptyStringSchema,
	profileId: uuidProfileIdSchema,
	lastActiveAt: ISO_DATE_TIME_SCHEMA
});
const nullableTrimmedStringSchema = union([trimmedNonEmptyStringSchema, nullFactory()]);
const organizationDirectoryAgentProfileWorkLocationSchema = strictObject({
	spaceId: string().trim().min(1).max(512),
	spaceName: trimmedNonEmptyStringSchema
});
const organizationDirectoryAgentProfileOperationalContextSchema = discriminatedUnion("status", [
	strictObject({ status: literal("checking") }),
	strictObject({
		status: literal("idle"),
		waitingCount: literal(0)
	}),
	strictObject({
		currentWorkLocation: organizationDirectoryAgentProfileWorkLocationSchema.optional(),
		queuedWorkLocations: array(organizationDirectoryAgentProfileWorkLocationSchema).max(50).optional(),
		status: literal("working"),
		waitingCount: number().int().min(0).max(50)
	}),
	strictObject({
		currentWorkLocation: organizationDirectoryAgentProfileWorkLocationSchema.optional(),
		queuedWorkLocations: array(organizationDirectoryAgentProfileWorkLocationSchema).max(50).optional(),
		recoveryAction: agentProfileWorkRecoveryActionKindSchema.optional(),
		recoveryTargetWakeLifecycleId: trimmedNonEmptyStringSchema.optional(),
		status: literal("paused"),
		waitingCount: number().int().min(0).max(50)
	})
]);
const organizationDirectoryHumanProfileSchema = strictObject({
	lastUsedAt: union([ISO_DATE_TIME_SCHEMA, nullFactory()]).optional(),
	profileId: uuidProfileIdSchema,
	profileName: trimmedNonEmptyStringSchema,
	profileHandle: trimmedNonEmptyStringSchema,
	status: enumFactory(["active", "disabled"])
});
const organizationDirectoryMemberSchema = strictObject({
	lastActiveAt: union([ISO_DATE_TIME_SCHEMA, nullFactory()]).optional(),
	memberId: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema,
	userId: trimmedNonEmptyStringSchema,
	role: trimmedNonEmptyStringSchema,
	joinedAt: ISO_DATE_TIME_SCHEMA,
	user: strictObject({
		id: trimmedNonEmptyStringSchema,
		name: trimmedNonEmptyStringSchema,
		email: trimmedNonEmptyStringSchema,
		image: nullableTrimmedStringSchema
	}),
	humanProfile: organizationDirectoryHumanProfileSchema.nullable()
});
const organizationDirectoryAgentProfileIdentitySchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	profileId: agentProfileIdSchema,
	profileName: trimmedNonEmptyStringSchema,
	profileHandle: trimmedNonEmptyStringSchema,
	profileKind: literal("agent"),
	isDefault: literal(false),
	status: enumFactory(["active", "disabled"]),
	ownerUserId: nullableTrimmedStringSchema,
	ownerName: trimmedNonEmptyStringSchema.optional(),
	avatarUrl: trimmedNonEmptyStringSchema.optional(),
	source: trimmedNonEmptyStringSchema,
	managedRole: union([authProfileManagedRoleSchema, nullFactory()]),
	createdByUserId: nullableTrimmedStringSchema,
	creatorProfileId: nullableTrimmedStringSchema,
	lastUsedAt: union([ISO_DATE_TIME_SCHEMA, nullFactory()]),
	lastTaskActivityAt: ISO_DATE_TIME_SCHEMA.nullable().optional(),
	createdAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA
});
const organizationDirectoryAgentProfileSchema = organizationDirectoryAgentProfileIdentitySchema.extend({
	isCenterAgent: boolean().optional(),
	operationalContext: organizationDirectoryAgentProfileOperationalContextSchema.optional(),
	readiness: getAgentExecutionReadinessResponseSchema
});
const organizationProfileDirectoryAgentProfileSchema = organizationDirectoryAgentProfileIdentitySchema.extend({
	agentControllerRef: nullableTrimmedStringSchema,
	modelSelection: agentProfileBindingModelSelectionSchema.nullable().optional(),
	isCenterAgent: boolean().optional()
});
const organizationProfileDirectoryReadOptionsSchema = strictObject({
	includeTaskActivity: boolean().optional(),
	includeMemberLastActiveAt: boolean().optional(),
	includeModelSelection: boolean().optional()
});
const organizationProfileDirectoryResponseSchema = strictObject({
	taskActivitySince: ISO_DATE_TIME_SCHEMA.optional(),
	organizationId: trimmedNonEmptyStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	members: array(organizationDirectoryMemberSchema),
	agentProfiles: array(organizationProfileDirectoryAgentProfileSchema)
});
const organizationAgentRuntimeOverviewItemSchema = strictObject({
	profileId: agentProfileIdSchema,
	lastTaskActivityAt: ISO_DATE_TIME_SCHEMA.nullable().optional(),
	operationalContext: organizationDirectoryAgentProfileOperationalContextSchema.optional(),
	readiness: getAgentExecutionReadinessResponseSchema
});
const organizationAgentRuntimeOverviewResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	taskActivitySince: ISO_DATE_TIME_SCHEMA.optional(),
	agentProfiles: array(organizationAgentRuntimeOverviewItemSchema)
});
const organizationDirectoryResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	generatedAt: ISO_DATE_TIME_SCHEMA,
	members: array(organizationDirectoryMemberSchema),
	agentProfiles: array(organizationDirectoryAgentProfileSchema)
});
const centerAgentOrganizationDirectoryPageSchema = organizationDirectoryResponseSchema.extend({
	nextCursor: string().min(1).max(1024).nullable(),
	totalMembers: number().int().nonnegative(),
	totalAgentProfiles: number().int().nonnegative()
});
const organizationMemberWebActivityResponseSchema = strictObject({
	lastActiveAt: ISO_DATE_TIME_SCHEMA,
	organizationId: trimmedNonEmptyStringSchema,
	userId: trimmedNonEmptyStringSchema
});
const CENTER_AGENT_ORGANIZATION_MEMBERS_LIST_DEFAULT_LIMIT = 20;
const CENTER_AGENT_ORGANIZATION_MEMBERS_LIST_MAX_LIMIT = 100;
const centerAgentListOrganizationMembersBodySchema = strictObject({
	cursor: string().trim().min(1).max(1024).optional(),
	limit: number().int().min(1).max(100).optional()
});
const centerAgentListOrganizationMembersToolRpcInputSchema = centerAgentListOrganizationMembersBodySchema.extend({
	context: openMeldRequestContextSchema,
	speakerMember: centerAgentToolSpeakerSchema.optional()
});
/**
* Bounded, member-only Organization read projection for Center Agent tools.
* It intentionally never resolves or returns Agent Profiles or their readiness;
* the broad `organizationDirectoryResponseSchema` remains the authority for
* callers that need Agent Profile identity and readiness.
*/
const centerAgentListOrganizationMembersResponseSchema = strictObject({
	generatedAt: ISO_DATE_TIME_SCHEMA,
	members: array(organizationDirectoryMemberSchema),
	nextCursor: string().trim().min(1).max(1024).nullable(),
	organizationId: trimmedNonEmptyStringSchema,
	totalMembers: number().int().nonnegative()
});
//#endregion
export { CENTER_AGENT_ORGANIZATION_MEMBERS_LIST_DEFAULT_LIMIT, CENTER_AGENT_ORGANIZATION_MEMBERS_LIST_MAX_LIMIT, centerAgentListOrganizationMembersBodySchema, centerAgentListOrganizationMembersResponseSchema, centerAgentListOrganizationMembersToolRpcInputSchema, centerAgentOrganizationDirectoryPageSchema, organizationAgentRuntimeOverviewItemSchema, organizationAgentRuntimeOverviewResponseSchema, organizationDirectoryAgentProfileIdentitySchema, organizationDirectoryAgentProfileOperationalContextSchema, organizationDirectoryAgentProfileSchema, organizationDirectoryAgentProfileWorkLocationSchema, organizationDirectoryHumanProfileSchema, organizationDirectoryMemberSchema, organizationDirectoryResponseSchema, organizationMemberWebActivityResponseSchema, organizationProfileDirectoryAgentProfileSchema, organizationProfileDirectoryReadOptionsSchema, organizationProfileDirectoryResponseSchema, spaceMemberWebActivityWriteSchema };

//# sourceMappingURL=organization-directory.js.map